import React, { useState, useEffect, useMemo, memo, useRef } from 'react';
import { Loader, Info, MousePointerClick, Trash2 } from 'lucide-react';
import * as XLSX from 'xlsx';

const XlsxMapper = ({ file, onChange }) => {
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [currentSheetIdx, setCurrentSheetIdx] = useState(0);
  
  const [sheetCache, setSheetCache] = useState({});
  const [isParsing, setIsParsing] = useState(false);

  const [selections, setSelections] = useState({});
  const [mode, setMode] = useState(null); 
  const [hoverCell, setHoverCell] = useState(null);

  // Drag & Drop state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);

  // Auto-scroll refs
  const scrollContainerRef = useRef(null);
  const scrollSpeedRef = useRef(0);
  const scrollRafRef = useRef(null);
  const mousePosRef = useRef({ x: 0, y: 0 });

  // 1. Parse File & Set Default Selection
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        setWorkbook(wb);
        setSheetNames(wb.SheetNames);
        setCurrentSheetIdx(0);
        setSheetCache({}); 
        setSelections({
            0: { id: { r: 0, c: 0 }, answers: { r: 0, cols: [1, 2, 3, 4, 5, 6] } }
        });
      } catch (err) {
        console.error("Error reading Excel file:", err);
      }
    };
    reader.readAsArrayBuffer(file);
  }, [file]);

  // 2. Parse Current Sheet
  useEffect(() => {
    if (!workbook) return;
    if (sheetCache[currentSheetIdx]) return;

    setIsParsing(true);
    const timer = setTimeout(() => {
        const sheetName = sheetNames[currentSheetIdx];
        const ws = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        
        setSheetCache(prev => ({ ...prev, [currentSheetIdx]: jsonData }));
        setIsParsing(false);
    }, 10);

    return () => clearTimeout(timer);
  }, [workbook, currentSheetIdx, sheetNames, sheetCache]);

  const currentSheetData = useMemo(() => sheetCache[currentSheetIdx] || [], [sheetCache, currentSheetIdx]);

  // 3. Grid Preview
  const maxCols = useMemo(() => {
    if (currentSheetData.length === 0) return 7;
    const maxInRows = Math.max(...currentSheetData.slice(0, 20).map(r => r.length));
    return Math.max(7, maxInRows);
  }, [currentSheetData]);

  const gridData = useMemo(() => {
    if (currentSheetData.length === 0) return [];
    const limitedRows = currentSheetData.slice(0, 20);
    const standardizedGrid = limitedRows.map(row => {
      const newRow = [...row];
      while (newRow.length < maxCols) newRow.push('');
      return newRow;
    });
    while (standardizedGrid.length < 20) {
      standardizedGrid.push(new Array(maxCols).fill(''));
    }
    return standardizedGrid;
  }, [currentSheetData, maxCols]);

  // A) Sledujeme pozici myši napříč celou obrazovkou
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
        mousePosRef.current = { x: e.clientX, y: e.clientY };
        
        if (!isDragging || mode !== 'pickingFruit') {
            scrollSpeedRef.current = 0;
            return;
        }

        const container = scrollContainerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const edgeSize = 80; 
        
        if (e.clientX < rect.left + edgeSize) {
            const intensity = Math.max(0, (rect.left + edgeSize) - e.clientX);
            scrollSpeedRef.current = -Math.min(25, intensity * 0.4); 
        } else if (e.clientX > rect.right - edgeSize) {
            const intensity = Math.max(0, e.clientX - (rect.right - edgeSize));
            scrollSpeedRef.current = Math.min(25, intensity * 0.4); 
        } else {
            scrollSpeedRef.current = 0; 
        }
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    return () => window.removeEventListener('mousemove', handleGlobalMouseMove);
  }, [isDragging, mode]);

  // B) Slučka pro samotný posun tabulky
  useEffect(() => {
    const scrollLoop = () => {
        if (scrollSpeedRef.current !== 0 && scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft += scrollSpeedRef.current;
            
            const el = document.elementFromPoint(mousePosRef.current.x, mousePosRef.current.y);
            if (el && el.tagName === 'TD') {
                const r = parseInt(el.getAttribute('data-r'));
                const c = parseInt(el.getAttribute('data-c'));
                if (!isNaN(r) && !isNaN(c)) {
                    setHoverCell(prev => (prev && prev.r === r && prev.c === c) ? prev : { r, c });
                    setDragEnd(prev => (prev && prev.r === r && prev.c === c) ? prev : { r, c });
                }
            }
        }
        scrollRafRef.current = requestAnimationFrame(scrollLoop);
    };

    if (isDragging && mode === 'pickingFruit') {
        scrollRafRef.current = requestAnimationFrame(scrollLoop);
    } else {
        if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
        scrollSpeedRef.current = 0;
    }

    return () => {
        if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, [isDragging, mode]);

  // 4. Global Mouse Up (VYBÍRÁNÍ ODPOVĚDÍ)
  useEffect(() => {
    const handleGlobalMouseUp = () => {
        if (isDragging && mode === 'pickingFruit') {
            setIsDragging(false);
            if (!dragStart || !dragEnd) return;

            const r = Math.min(dragStart.r, dragEnd.r);
            const cMin = Math.min(dragStart.c, dragEnd.c);
            const cMax = Math.max(dragStart.c, dragEnd.c);
            
            const cols = [];
            for (let i = cMin; i <= cMax; i++) cols.push(i);

            setSelections(prev => {
                // Deep copy pro bezpečné mazání ze všech listů
                const newState = JSON.parse(JSON.stringify(prev));
                
                // Zrušíme staré odpovědi ze VŠECH listů
                Object.keys(newState).forEach(sheetKey => {
                    if (newState[sheetKey]) {
                        delete newState[sheetKey].answers;
                        // Pokud nezbylo ani ID, smažeme celý objekt listu
                        if (Object.keys(newState[sheetKey]).length === 0) {
                            delete newState[sheetKey];
                        }
                    }
                });

                // Přidáme nové odpovědi do aktuálního listu
                newState[currentSheetIdx] = {
                    ...(newState[currentSheetIdx] || {}),
                    answers: { r, cols }
                };
                return newState;
            });
            
            setMode(null);
            setDragStart(null);
            setDragEnd(null);
        }
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging, mode, dragStart, dragEnd, currentSheetIdx]);

  // 5. REACTIVE CSV GENERATION
  useEffect(() => {
    if (!workbook) return;

    const allExtractedRows = [];
    let hasAnyData = false;

    Object.entries(selections).forEach(([sheetIdxStr, selection]) => {
      // ID už není povinné, ale odpovědi ano.
      if (!selection || !selection.answers) return;

      const sheetIdx = parseInt(sheetIdxStr);
      const sheetName = sheetNames[sheetIdx];
      if (!sheetName) return;

      let data = sheetCache[sheetIdx];
      if (!data) {
        const ws = workbook.Sheets[sheetName];
        data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      }

      if (!data || data.length === 0) return;

      const { id: idSel, answers: ansSel } = selection;

      if (!ansSel || !ansSel.cols || ansSel.cols.length === 0) return;

      // Řádky určujeme podle oblasti odpovědí.
      // ID sloupec je jen doplněk, nemusí existovat.
      for (let i = ansSel.r; i < data.length; i++) {
        const row = data[i] || [];

        const idVal =
          idSel && i >= idSel.r
            ? String(row[idSel.c] || '').trim()
            : '';

        const answers = [];

        ansSel.cols.forEach(cIndex => {
          const val = String(row[cIndex] || '').trim();
          if (val) answers.push(val);
        });

        if (answers.length > 0) {
          hasAnyData = true;

          answers.forEach(ans => {
            allExtractedRows.push(
              `"${idVal.replace(/"/g, '""')}","${ans.replace(/"/g, '""')}"`
            );
          });
        }
      }
    });

    if (hasAnyData) {
      const csvContent = allExtractedRows.join('\n');
      const csvFile = new File([csvContent], "combined_import.csv", {
        type: "text/csv",
      });

      onChange(csvFile);
    } else {
      onChange(null);
    }
  }, [selections, workbook, sheetNames, sheetCache, onChange]);

  // MOUSE EVENT HANDLERS (VYBÍRÁNÍ ID)
  const handleMouseDown = (r, c) => {
    if (!mode) return;
    
    if (mode === 'pickingId') {
        setSelections(prev => {
            // Deep copy pro bezpečné mazání ze všech listů
            const newState = JSON.parse(JSON.stringify(prev));
            
            // Zrušíme staré ID ze VŠECH listů
            Object.keys(newState).forEach(sheetKey => {
                if (newState[sheetKey]) {
                    delete newState[sheetKey].id;
                    // Pokud nezbyly ani odpovědi, smažeme celý objekt listu
                    if (Object.keys(newState[sheetKey]).length === 0) {
                        delete newState[sheetKey];
                    }
                }
            });

            // Přidáme nové ID do aktuálního listu
            newState[currentSheetIdx] = {
                ...(newState[currentSheetIdx] || {}),
                id: { r, c }
            };
            return newState;
        });
        setMode(null);
    } else if (mode === 'pickingFruit') {
        setIsDragging(true);
        setDragStart({ r, c });
        setDragEnd({ r, c });
    }
  };

  const handleMouseEnter = (r, c) => {
      setHoverCell({ r, c });
      if (mode === 'pickingFruit' && isDragging) {
          setDragEnd({ r, c });
      }
  };

  const getCellClass = (r, c) => {
    const currentSel = selections[currentSheetIdx] || {};
    const activeId = currentSel.id;
    const activeFruit = currentSel.answers;

    let isId = false;
    let isFruit = false;

    if (mode === 'pickingId' && hoverCell && c === hoverCell.c && r >= hoverCell.r) {
        isId = true;
    } else if (activeId && c === activeId.c && r >= activeId.r) {
        isId = true;
    }

    if (mode === 'pickingFruit' && isDragging && dragStart && dragEnd) {
        const rMin = Math.min(dragStart.r, dragEnd.r);
        const cMin = Math.min(dragStart.c, dragEnd.c);
        const cMax = Math.max(dragStart.c, dragEnd.c);
        if (c >= cMin && c <= cMax && r >= rMin) {
            isFruit = true;
        }
    } else if (mode === 'pickingFruit' && !isDragging && hoverCell && c === hoverCell.c && r >= hoverCell.r) {
        isFruit = true;
    } else if (activeFruit && activeFruit.cols && activeFruit.cols.includes(c) && r >= activeFruit.r) {
        isFruit = true;
    }

    if (isId) return 'bg-yellow-200 border-yellow-400 text-yellow-900';
    if (isFruit) return 'bg-blue-200 border-blue-400 text-blue-900 shadow-inner z-10';
    return 'bg-white border-gray-200 hover:bg-gray-50';
  };

  const clearSelection = () => {
      setSelections(prev => {
          const newState = { ...prev };
          delete newState[currentSheetIdx];
          return newState;
      });
  };

  if (!workbook) return <div className="p-8 flex justify-center"><Loader className="animate-spin text-blue-600 h-8 w-8" /></div>;

  const currentSelection = selections[currentSheetIdx] || {};

  return (
    <div className="w-full bg-white rounded-lg border border-gray-200 shadow-sm p-4 animate-fade-in mt-4">
      
      {/* TEXT LISTŮ A TLAČÍTKA LISTŮ */}
      <div className="mb-5">
        <h3 className="text-lg font-bold text-gray-800 mb-3">Mapování sloupců</h3>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide mr-1">
            Listy v souboru:
          </span>
          {sheetNames.map((name, idx) => {
             // ZMĚNA: Fajfka se ukáže, pokud je v listu vybráno buď ID nebo Odpovědi
             const hasSelection = selections[idx] && (selections[idx].id || selections[idx].answers);
             return (
                <button
                key={name}
                onClick={() => setCurrentSheetIdx(idx)}
                className={`px-3 py-1.5 text-sm rounded-md font-medium border transition-all ${
                    currentSheetIdx === idx 
                    ? 'bg-gray-800 text-white border-gray-800 shadow-md' 
                    : hasSelection 
                        ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' 
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
                >
                {name} {hasSelection && ' ✓'}
                </button>
             );
          })}
        </div>
        <div className="text-sm text-gray-600 flex items-start mt-2">
          <Info className="w-5 h-5 mr-2 flex-shrink-0 text-gray-500 mt-0.5" />
          <div className="space-y-1.5">
              <p>Zde můžete přepínat mezi listy v nahraném souboru.</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200 shadow-inner">

        <div className="text-sm text-gray-600 flex items-start mt-2">
            <Info className="w-5 h-5 mr-2 flex-shrink-0 text-gray-500 mt-0.5" />
            <div className="space-y-1.5">
                <p><b>1. Vyberte oblast ID:</b> Klikněte na tlačítko 'Vybrat sloupec ID' a poté kliknutím označte v jaké buňce začíná série ID respondentů (označenou žlutou barvou). Pokud nemáte id respondentů, zrušte výběr a vyberte pouze odpovědi.</p>
                <p><b>2. Vyberte oblast odpovědí:</b> Klikněte na tlačítko 'Vybrat oblast odpovědí' a poté kliknutím a tažením označte oblast odpovědí respondentů (označeno modrou barvou). Jeden respondent může mít více odpovědí. </p>
            </div>
        </div>
        
        {/* HLAVNÍ TLAČÍTKA */}
        <div className="flex flex-col sm:flex-row gap-3 w-full border-t border-gray-200 pt-3">
            <button 
              onClick={() => { setMode(mode === 'pickingId' ? null : 'pickingId'); setHoverCell(null); }}
              className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg text-sm font-bold shadow-sm transition-all ${
                  mode === 'pickingId' 
                  ? 'ring-2 ring-yellow-400 bg-yellow-100 text-yellow-800 translate-y-0.5' 
                  : 'bg-yellow-400 text-yellow-900 hover:bg-yellow-500 hover:text-white'
                }`}
            >
              <MousePointerClick className="w-5 h-5 mr-2" />
              {mode === 'pickingId' ? 'Klikněte na sloupec ID...' : 'Vybrat sloupec ID'}
            </button>

            <button 
              onClick={() => { setMode(mode === 'pickingFruit' ? null : 'pickingFruit'); setHoverCell(null); }}
              className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg text-sm font-bold shadow-sm transition-all ${
                  mode === 'pickingFruit' 
                  ? 'ring-2 ring-blue-400 bg-blue-100 text-blue-800 translate-y-0.5' 
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
            >
              <MousePointerClick className="w-5 h-5 mr-2" />
              {mode === 'pickingFruit' ? 'Tážením označte odpovědi...' : 'Vybrat oblast odpovědí'}
            </button>
        </div>

        {/* TLAČÍTKO ZRUŠIT VÝBĚR */}
        {(currentSelection.id || currentSelection.answers) && (
            <div className="flex justify-center w-full mt-1">
                <button 
                    onClick={clearSelection}
                    className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold text-red-600 bg-white border border-red-200 hover:bg-red-50 hover:border-red-300 transition-colors shadow-sm"
                    title="Zrušit označení v aktuálním listu"
                >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Zrušit výběr
                </button>
            </div>
        )}
      </div>

      <div 
        ref={scrollContainerRef} 
        className="overflow-x-auto border border-gray-300 rounded-lg cursor-crosshair relative bg-white select-none shadow-inner" 
        style={{ maxHeight: '400px' }}
      >
        {isParsing && (
            <div className="absolute inset-0 bg-white/80 z-20 flex items-center justify-center backdrop-blur-sm">
                <div className="flex items-center text-blue-600 font-semibold bg-white p-3 rounded-lg shadow-lg border border-gray-100">
                    <Loader className="h-6 w-6 mr-3 animate-spin" />
                    Načítám list...
                </div>
            </div>
        )}
        <table className="min-w-full text-xs text-center border-collapse">
          <thead className="sticky top-0 z-10 shadow-sm">
            <tr>
               <th className="p-2 bg-gray-100 border-b border-r border-gray-300 w-10 sticky left-0 z-20">#</th>
               {Array.from({ length: maxCols }).map((_, i) => (
                 <th key={i} className="p-2 bg-gray-100 border-b border-gray-300 font-bold text-gray-600 min-w-[80px]">
                    {String.fromCharCode(65 + i)}
                 </th>
               ))}
            </tr>
          </thead>
          <tbody>
            {gridData.map((row, rIdx) => (
              <tr key={rIdx}>
                <td className="p-1.5 bg-gray-50 border-r border-b border-gray-300 font-mono text-gray-500 sticky left-0 z-10 font-bold">
                  {rIdx + 1}
                </td>
                {row.map((cellVal, cIdx) => (
                  <td 
                    key={cIdx}
                    data-r={rIdx} 
                    data-c={cIdx} 
                    onMouseEnter={() => handleMouseEnter(rIdx, cIdx)}
                    onMouseLeave={() => setHoverCell(null)}
                    onMouseDown={() => handleMouseDown(rIdx, cIdx)}
                    className={`p-1.5 border border-gray-200 transition-colors duration-75 max-w-[120px] truncate ${getCellClass(rIdx, cIdx)}`}
                    title={cellVal}
                  >
                    {cellVal}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default memo(XlsxMapper);